import {t} from '../../i18n'
import Primary from "../layouts/Primary";

import './Rankings.scss'
import {useMemo, useReducer} from "react";
import ScrollingTable, {ColumnDescription} from "../components/ScrollingTable";
import {DisplayConfig} from "../Config";
import { useContext } from "react";
import { DivisionRankingData, RankingData } from "../rankingDataState";
import DisplayParams, { DivisionInfo } from "../DisplayParams";
import {ReactSVG} from "react-svg";
import MiniBracket from "../components/MiniBracket";
import Bracket from "./Bracket";
import EventLogo from "../components/EventLogo";
import {QRCodeSVG} from "qrcode.react";
import {useInterval} from "../../hooks";

function Rankings({state, narrow, short, division, smallText, allTeams, missingLeague} : {
    state: DivisionRankingData,
    narrow?: boolean,
    division?: DivisionInfo,
    short?: boolean,
    smallText?: boolean,
    allTeams?: boolean,
    missingLeague?: boolean
}) {
    let rankingsTableHeight = '810px';
    let resultsTableHeight = '810px';

    const qrTarget = state.url ? state.url : null;
    const qrElement = qrTarget && division && !short ? <QRCodeSVG size={140} value={qrTarget} bgColor={"#000000"} fgColor={"#FFFFFF"}/> : null;
    if(division) {
        if(short && narrow) {
            rankingsTableHeight = '442px';
            resultsTableHeight = '377px';
        } else if(short) {
            rankingsTableHeight = '430px';
            resultsTableHeight = '430px';
        } else {
            rankingsTableHeight = '925px';
            resultsTableHeight = qrElement ? '635px' : '850px';
        }
    }
    const rankingsList = (allTeams && state.allRankings) || state.rankings || []
    if(missingLeague) {
        rankingsList.forEach(r => {
            r.rank = '-'
        })
        rankingsList.sort((a, b) => parseInt(a.number) - parseInt(b.number) || (isNaN(parseInt(a.number)) ? 1 : 0) - (isNaN(parseInt(b.number)) ? 1 : 0))
    }
    const hasLeagueRankings = rankingsList.some(r => r.leagueRank !== '-')
    let lastUpdated = null;
    if(state.lastUpdatedAt > 0 && !narrow) {
        const difference = Date.now() - state.lastUpdatedAt;
        const within24Hours = difference < 1000 * 60 * 60 * 24;
        const format = new Intl.DateTimeFormat(document.documentElement.lang, { dateStyle: within24Hours ? undefined : 'medium', timeStyle: 'short' });
        lastUpdated = format.format(state.lastUpdatedAt);
    }
    const gridRankings = useMemo(() => {
        return rankingsList.map(e => ({...e}))
    }, [rankingsList])

    const gridMatchList = useMemo(() => {
        return (state.matchList.map((m) => {
            const isRedWin = m.winner === 'R';
            const isBlueWin = m.winner === 'B';
            return ({...m,
                result: m.redScore + '-' + m.blueScore + ' ' + m.winner,
                className: (isRedWin ? 'red-wins' : (isBlueWin ? 'blue-wins' : 'tie'))
            })
        }))
    }, [state.matchList])

    const matchCount = state.matchCount;
    const matchesPlayed = state.matchesPlayed;

    return <div className={`rankingsWrapper ${division ? "divisionWrapper" : ''} ${narrow ? "narrowRankings" : 'wideRankings'}`} >
        <div className="rankingsHeader">
            <span className="rankTimestamp">{lastUpdated ? t`Last updated at: ${lastUpdated}` : ''}</span>
        </div>
        {division ? <div className="dualDivisionHeading">
                {narrow ? null : <span className="rankMatchesPerTeam">{state.matchesPerTeam > 0 ? t`${state.matchesPerTeam} matches per team`: ''}{missingLeague ? t` (league rankings unavailable)` : ''}</span>}
                <EventLogo logo={division.logo} /> {t`${division.name} Division`}
                {narrow ? null : <span className="rankMatchCount">{matchCount > 0 ? t`${matchesPlayed} / ${matchCount} matches played`: ''}</span>}
            </div> : null }
        <div className="d-flex flex-row justify-content-around">
            <div className={`rankingsTableWrapper rankingsList quals ${smallText ? "smallText" : ''} ${hasLeagueRankings ? 'withLeague' : ''}`}>
                {<ScrollingTable highlightChanges key={`rankTable-${smallText ? 'small' : 'large'}`}
                    width={narrow ? '530px' : '1050px'}
                    height={rankingsTableHeight}
                    rowSpacing={10}
                    keyCol={'number'}
                    columns={[
                        {
                            name: narrow ? 'Rk' : (hasLeagueRankings ? t`Event Rank` : t`Rank`),
                            field: 'rank'
                        },
                        (hasLeagueRankings ? {
                            name: t`League Rank`,
                            field: 'leagueRank',
                        } : null),
                        {
                            name: 'Team',
                            field: 'number',
                        },
                        {
                            name: 'RS',
                            field: 'rp',
                        },
                        {
                            name: narrow ? 'PTS' : 'POINTS',
                            field: 'tbp1'
                        },
                        {
                            name: 'BASE',
                            field: 'tbp2'
                        },
                        {
                            name: narrow ? '#' : t`Plays`,
                            field: 'matchesPlayed'
                        },
                    ].filter((d) => d !== null)
                     .filter((c): c is ColumnDescription => c !== null && (!narrow || c.field !== 'tbp2'))}
                    data={gridRankings}
                />}
            </div>
            <div className={`rankingsTableWrapper resultsList quals ${smallText ? "smallText" : ''}`}>
                {/* Use key to force a reinit of the component if the font size changed */}
                <ScrollingTable highlightChanges  key={`resultsTable-${smallText ? 'small' : 'large'}`}
                                stickyFooter={2}
                    width={narrow ? '330px' : '700px'}
                    height={resultsTableHeight}
                    keyCol={'number'}
                    classCol={'className'}
                    noHeading
                    rowSpacing={10}
                    columns={[
                        {
                            name: t`Match`,
                            field: 'number'
                        },
                        {
                            name: t`Red Score`,
                            field: 'redScore'
                        },
                        {
                            name: t`Red Teams`,
                            field: 'redTeams'
                        },
                        {
                            name: t`Blue Teams`,
                            field: 'blueTeams'
                        },
                        {
                            name: t`Blue Score`,
                            field: 'blueScore'
                        }
                    ].filter((d) => !narrow || (d.field !== 'redTeams' && d.field !== 'blueTeams'))}
                    data={gridMatchList}
                />
                {qrElement && qrTarget ? <div className={"onlineResultsLink"}>
                    <span>Online Results:</span><br/>
                    {qrElement}<br/>
                    {qrTarget.replace("https://", "")}
                </div> : null}
            </div>
        </div>
        {division && !narrow ? null : <div className="rankingsFooter">
            <span className="rankMatchesPerTeam">{state.matchesPerTeam > 0 ? t`${state.matchesPerTeam} matches per team`: ''}{missingLeague ? t` (league rankings unavailable)` : ''}</span>
            <span className="rankMatchCount">{matchCount > 0 ? t`${matchesPlayed} / ${matchCount} matches played`: ''}</span>
        </div>}
    </div>
}

export default function RankingsPage({ config, missingLeague} : {
    config: DisplayConfig,
    missingLeague?: boolean
}) {
    const { isDualDivisionParent, divisions, divisionNumber } = useContext(DisplayParams);
    const state = useContext(RankingData);

    // Should not happen except via bug
    if(divisions == undefined || divisionNumber == undefined) {
        return <Primary>
            {t`Rankings are not supported on this display`}
        </Primary>
    }

    const smallText = config.rankingsFontSize === 'smaller';
    let divisionOrder: number[];
    if(isDualDivisionParent && divisions) {
        divisionOrder = (config.divisionOrder || Object.keys(divisions).map(x => parseInt(x, 10)).sort((a, b) => a - b)).filter(x => divisions[x]);
    } else {
        divisionOrder = [divisionNumber];
    }
    const narrow = divisionOrder.length > 2 || divisionOrder.length == 2 && config.dualDivisionRankingStyle === 'sideBySide';
    const short = divisionOrder.length > 2 || divisionOrder.length == 2 && config.dualDivisionRankingStyle === 'stacked';

    return <>
        <Primary qr={isDualDivisionParent && divisions ? "/" : "/rankings"} compact={isDualDivisionParent && !!divisions}>
            <div className={`h-100 w-100`}>
                {divisionOrder.map((d, idx) =>
                    (state[d] ?
                        <Rankings state={state[d]} division={divisions[d]} narrow={narrow} short={short} smallText={smallText} allTeams={config.rankingsAllTeams} missingLeague={missingLeague} key={idx} /> : null)
                )}
            </div>
        </Primary>
    </>;
}